import { useEffect, useRef } from "react";
import gsap from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";

gsap.registerPlugin(ScrollTrigger);

// Increased widths for massive impact
const COMPANIES = [
  { name: "NOBLE" },
  { name: "FUSE" },
  { name: "VOLTAGE" },
  { name: "DEVELOPER DAO" },
  { name: "VARIANCE" },
  { name: "DAOS.FUN" },
];

// 6x Duplication to ensure infinite scroll illusion at high speeds
const MARQUEE_ITEMS = [
  ...COMPANIES,
  ...COMPANIES,
  ...COMPANIES,
  ...COMPANIES,
  ...COMPANIES,
  ...COMPANIES,
];

const CompanyLogo = ({
  name,
  className,
}: {
  name: string;
  className?: string;
}) => (
  <span
    className={`text-3xl md:text-[2.5rem] lg:text-5xl font-semibold tracking-tight whitespace-nowrap ${className}`}
    style={{ fontFamily: "Inter, system-ui, sans-serif" }}
  >
    {name}
  </span>
);

const ABOUT_TEXT =
  "i am driven by curiosity and craft and i care about clean architecture, healthy abstractions and code that stays readable as it grows. I Build Wallets. Payments. On-chain flows. Digital experiences with a passion for simplicity and making complex problems elegant products";

const CIPHER_CHARS = "ABCDEFGHIJKLMNOPQRSTUVWXYZ";
const randomChar = () =>
  CIPHER_CHARS[Math.floor(Math.random() * CIPHER_CHARS.length)]!;

export const About = () => {
  const sectionRef = useRef<HTMLDivElement>(null);
  const textContainerRef = useRef<HTMLParagraphElement>(null);
  const col1Ref = useRef<HTMLDivElement>(null);
  const col2Ref = useRef<HTMLDivElement>(null);
  const tickerRef = useRef<(() => void) | null>(null);
  const decryptTickerRef = useRef<(() => void) | null>(null);
  const philoBorderTopRef = useRef<HTMLDivElement>(null);
  const philoBorderBottomRef = useRef<HTMLDivElement>(null);
  const philoContentRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const ctx = gsap.context(() => {
      // 1. DECRYPTION TEXT REVEAL — pure DOM, no React state
      const container = textContainerRef.current;
      if (!container) return;

      const chars = ABOUT_TEXT.split("");
      const spans: HTMLSpanElement[] = [];

      // Build DOM: one span per character, spaces get fixed width
      container.innerHTML = "";
      chars.forEach((char) => {
        const span = document.createElement("span");
        if (char === " ") {
          span.className = "inline-block w-[0.35em]";
          span.textContent = " ";
        } else {
          span.className = "text-muted-foreground/40";
          span.textContent = randomChar();
        }
        container.appendChild(span);
        spans.push(span);
      });

      // Trigger decryption when 60% in view
      ScrollTrigger.create({
        trigger: container,
        start: "top 60%",
        once: true,
        onEnter: () => {
          const duration = 3000;
          const startTime = Date.now();
          const resolved = new Set<number>();
          const totalChars = chars.filter((c) => c !== " ").length;

          // Each char gets a random resolve time
          const resolveTimes = chars.map((c) =>
            c === " " ? 0 : Math.random() * duration,
          );

          function decryptTick() {
            const elapsed = Date.now() - startTime;

            for (let i = 0; i < chars.length; i++) {
              if (chars[i] === " " || resolved.has(i)) continue;

              const span = spans[i];
              const resolveTime = resolveTimes[i];
              if (!span || resolveTime === undefined) continue;

              if (elapsed >= resolveTime) {
                span.textContent = chars[i]!;
                span.className = "text-foreground";
                resolved.add(i);
              } else {
                span.textContent = randomChar();
              }
            }

            if (resolved.size >= totalChars) {
              gsap.ticker.remove(decryptTick);
              decryptTickerRef.current = null;
            }
          }

          gsap.ticker.add(decryptTick);
          decryptTickerRef.current = decryptTick;
        },
      });

      // 2. KINETIC COLUMNS — single ticker, one source of truth
      const col1 = col1Ref.current;
      const col2 = col2Ref.current;
      if (!col1 || !col2) return;

      const BASE_SPEED = 0.4;
      let col1Pos = 0;
      let col2Pos = 0;
      let scrollBoost = 0;
      let lastScrollTime = 0;

      // Measure one loop of items (1/6th of total since we have 6x duplication)
      const inner1 = col1.firstElementChild as HTMLElement;
      const inner2 = col2.firstElementChild as HTMLElement;
      const oneLoopHeight = inner1.scrollHeight / 6;
      const oneLoopWidth = inner1.scrollWidth / 6;

      // Track scroll velocity near this section
      ScrollTrigger.create({
        trigger: sectionRef.current,
        start: "top bottom",
        end: "bottom top",
        onUpdate: (self) => {
          scrollBoost = Math.min(Math.abs(self.getVelocity()) / 50, 40);
          lastScrollTime = Date.now();
        },
      });

      const kineticTick = () => {
        // Hold speed for 500ms after last scroll, then decay
        const timeSinceScroll = Date.now() - lastScrollTime;
        if (timeSinceScroll > 500) {
          scrollBoost *= 0.9;
        }

        const speed = BASE_SPEED + scrollBoost;
        const isDesktop = window.innerWidth >= 1024;

        // Col1 drifts up (desktop) / left (mobile)
        col1Pos -= speed;
        // Col2 drifts down (desktop) / right (mobile)
        col2Pos += speed;

        // Wrap positions for infinite loop
        if (isDesktop) {
          if (col1Pos < -oneLoopHeight) col1Pos += oneLoopHeight;
          if (col2Pos > oneLoopHeight) col2Pos -= oneLoopHeight;
          col1.style.transform = `translateY(${col1Pos}px)`;
          col2.style.transform = `translateY(${col2Pos}px)`;
        } else {
          if (col1Pos < -oneLoopWidth) col1Pos += oneLoopWidth;
          if (col2Pos > oneLoopWidth) col2Pos -= oneLoopWidth;
          col1.style.transform = `translateX(${col1Pos}px)`;
          col2.style.transform = `translateX(${col2Pos}px)`;
        }
      };

      // 3. PHILOSOPHY REVEAL
      const phTop = philoBorderTopRef.current;
      const phBot = philoBorderBottomRef.current;
      const phContent = philoContentRef.current;

      if (phTop && phBot && phContent) {
        gsap.set([phTop, phBot], { scaleX: 0 });
        gsap.set(phContent, { height: 0, autoAlpha: 0 });

        ScrollTrigger.create({
          trigger: phTop,
          start: "top 65%",
          once: true,
          onEnter: () => {
            const tl = gsap.timeline();
            tl.to(phTop, { scaleX: 1, duration: 1.2, ease: "power3.inOut" })
              .to(
                phContent,
                {
                  height: "auto",
                  autoAlpha: 1,
                  duration: 1.5,
                  ease: "expo.inOut",
                },
                "-=0.8",
              )
              .to(
                phBot,
                { scaleX: 1, duration: 1.2, ease: "power3.inOut" },
                "-=0.8",
              );
          },
        });
      }

      gsap.ticker.add(kineticTick);
      tickerRef.current = kineticTick;
    }, sectionRef);

    return () => {
      ctx.revert();
      if (tickerRef.current) gsap.ticker.remove(tickerRef.current);
      if (decryptTickerRef.current)
        gsap.ticker.remove(decryptTickerRef.current);
    };
  }, []);

  return (
    <section
      ref={sectionRef}
      className="relative z-10 w-full bg-background min-h-screen shadow-[0_-12px_15px_var(--background)] overflow-hidden flex items-center py-32"
      data-section="about"
    >
      <div className="container mx-auto px-6 w-full space-y-12 overflow-hidden">
        <div className="mb-16 md:mb-20">
          <div className="text-base text-muted-foreground font-mono uppercase tracking-[0.2em] border-l-2 border-blue-500 pl-4 text-left">
            01 / About
          </div>
        </div>

        <div className="flex flex-col lg:flex-row gap-12 lg:gap-24 items-center h-full md:max-h-[724px]">
          {/* LEFT: Text Reveal */}
          <div className="w-full lg:w-[75%] xl:w-[65%]">
            <p
              ref={textContainerRef}
              className="text-3xl md:text-4xl lg:text-[2.7rem] xl:text-5xl font-medium leading-[1.2] tracking-tight font-mono"
            />
          </div>

          {/* RIGHT: Kinetic Columns / Rows */}
          <div className="w-full lg:w-[25%] xl:w-[35%] h-full flex flex-col lg:flex-row justify-center items-center gap-6 lg:gap-8 xl:gap-12 relative opacity-60 mask-image-gradient overflow-hidden max-h-[600px] lg:max-h-full lg:pt-0 pt-10">
            {/* Item Group 1 */}
            {/* Mobile: Row (Horizontal) | Desktop: Column (Top -> Bottom) */}
            <div
              ref={col1Ref}
              className="flex gap-12 items-center will-change-transform w-max lg:w-auto"
              style={{ writingMode: "horizontal-tb" }} // Default reset for mobile
            >
              <div className="flex gap-12 items-center lg:[writing-mode:vertical-rl]">
                {MARQUEE_ITEMS.map((company, i) => (
                  <div
                    key={`c1-${i}`}
                    className="flex items-center justify-center shrink-0"
                  >
                    <CompanyLogo
                      name={company.name}
                      className="text-foreground/80"
                    />
                  </div>
                ))}
              </div>
            </div>

            {/* Item Group 2 */}
            {/* Mobile: Row (Horizontal) | Desktop: Column (Bottom -> Top) */}
            <div
              ref={col2Ref}
              className="flex gap-12 items-center will-change-transform w-max lg:w-auto"
              style={{ writingMode: "horizontal-tb" }} // Default reset for mobile
            >
              <div className="flex gap-12 items-center lg:[writing-mode:vertical-rl] lg:rotate-180">
                {MARQUEE_ITEMS.map((company, i) => (
                  <div
                    key={`c2-${i}`}
                    className="flex items-center justify-center shrink-0"
                  >
                    <CompanyLogo
                      name={company.name}
                      className="text-foreground/80"
                    />
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>

        <div className="space-y-4">
          {/* Philosophy */}
          <div
            ref={philoBorderTopRef}
            className="lg:w-[75%] xl:w-[65%] h-0.5 bg-muted-foreground/40 origin-left"
          ></div>
          <div
            ref={philoContentRef}
            className="overflow-hidden lg:w-[75%] xl:w-[65%]"
          >
            <div className="py-6 w-full">
              <span className="text-xs text-muted-foreground font-bold uppercase tracking-[0.3em] block mb-4 rounded-xl px-2.5 py-1 w-fit border border-muted-foreground/40">
                my philosophy
              </span>
              <p className="text-lg md:text-xl text-muted-foreground font-mono leading-relaxed italic">
                Error correcting: A shift in state doesn't break the system's
                truth.
              </p>
              <span className="text-sm text-blue-500 font-mono mt-3 block">
                — code ℤ2
              </span>
            </div>
          </div>
          <div
            ref={philoBorderBottomRef}
            className="lg:w-[75%] xl:w-[65%] h-0.5 bg-muted-foreground/40 origin-left"
          ></div>
        </div>
      </div>
    </section>
  );
};
